<template>
  <v-dialog
    v-model="dialog"
    max-width="1200"
  >
    <v-card>
      <v-card-title class="text-h5 z-card-title">
        <v-icon left>
          mdi-truck-fast-outline
        </v-icon>
        {{ $t("Common.alternative_address") + (customerName ? (": " + customerName) : "") }}
        <v-spacer></v-spacer>
        <v-btn
          icon
          @click="closeDialog"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="mt-5">
        <v-autocomplete
          v-model="selectedAddress"
          :items="addressSuggestions"
          :no-filter="true"
          :label="$t('Customer.enter_new_address')"
          :search-input.sync="searchText"
          item-text="label"
          :return-object="true"
          :hide-no-data="true"
          :loading="isLoading"
          :no-data-text="$t('Common.no_data')"
        ></v-autocomplete>


        <MapLibre
          v-if="isVisible"
          :markers="selectedCoordinates ? [selectedCoordinates] : []"
        ></MapLibre>

        <div
          v-if="alertText"
          class="alert-container mt-5"
        >
          <v-alert
            dense
            type="error"
          >
            {{ alertText }}
          </v-alert>
        </div>

        <v-card-actions
          class="d-flex justify-center"
        >
          <v-btn
            class="mx-0"
            color="zwonogreen"
            :loading="isSaving"
            :disabled="!selectedCoordinates"
            @click="saveAlternativeAddress"
          >
            {{ $t("Common.save") }}
          </v-btn>

          <v-btn 
            class="mx-0 ml-5"
            color="warning"
            :loading="isSaving"
            @click="deleteAlternativeAddressLocal"
          >
            {{ $t("Common.delete_alternative_address") }}
          </v-btn>
        </v-card-actions>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import MapLibre from "@/components/maplibre/MapLibre.vue";
import { mapActions } from "vuex";
import {
  fetchAddressSuggestionsPlanPlusV2,
  fetchLocationDetailsPlanPlusV2,
  fetchStreetDetailsPlanPlusV2
} from "../../helpers/address-search-helpers.js";
import { mapGetters } from "vuex";

export default {
  components: {
    MapLibre,
  },
  props: {
    isVisible: {
      type: Boolean,
      default: false,
      required: true
    },
    orderId: {
      type: String,
      required: true
    },
    customerName: {
      type: String,
      required: true
    },
    isUserChange: {
      type: Boolean,
      default: false,
      required: false
    },
  },
  data () {
    return {
      dialog: this.isVisible,
      searchText: null,
      selectedAddress: null,
      selectedCoordinates: null,
      foundLocation: null,
      addressSuggestions: [],
      isLoading: false,
      isSaving: false,
      alertText: '',
    }
  },
  computed: {
    ...mapGetters(["getSearchAddressApi"]),
  },
  watch: {
    isVisible (value) {
      this.dialog = value
    },
    dialog (value) {
      this.$emit('is-visible', value)
    },
    searchText (text) {
      if (this.isVisible && text) this.fetchAddressSuggestions(text)
    },
    async selectedAddress(value) {
      if (!value || !value?.value?.token) return;

      // Extract house number from search text if present
      const houseNumber = this.extractHouseNumber(this.searchText);

      // If it's a street and we have a house number, resolve exact coordinates
      if (value?.value?.type === 'street' && houseNumber) {
        this.selectedCoordinates = null;
        await this.resolveStreetDetails(value.value.token, houseNumber);
        return;
      }

      // If it's a street but no house number, show message
      if (value?.value?.type === 'street' && !houseNumber) {
        this.alertText = 'Please include house number in the address (e.g., "Golsvordijeva 28")';
        this.selectedCoordinates = null;
        return;
      }

      // Non-street: resolve details directly
      this.isLoading = true;
      try {
        const details = await fetchLocationDetailsPlanPlusV2(value.value.token);

        if (!details) {
          this.alertText = 'Failed to fetch location details';
          this.foundLocation = {
            apiType: 'Planplus',
            address1: value.value.title || value.value.address,
            city: value.value.title || value.value.address,
            country: 'RS',
          };
          this.selectedCoordinates = null;
          return;
        }

        this.foundLocation = {
          apiType: 'Planplus',
          country: details.country || 'RS',
          city: details.place || details.municipality || '',
          postal_code: details.postalCode || null,
          address1: this.buildFullAddress(details, houseNumber),
          lat: details.lat || null,
          lon: details.lng || null,
        };

        if (!details.lat || !details.lng) {
          this.alertText = 'Location found but coordinates missing. Please select location on map.';
          this.selectedCoordinates = null;
        } else {
          this.selectedCoordinates = { lat: details.lat, lon: details.lng };
          this.alertText = '';
        }
      } catch (e) {
        this.alertText = 'Error fetching location details';
        this.selectedCoordinates = null;
        this.foundLocation = {
          apiType: 'Planplus',
          address1: value?.value?.title || value?.value?.address,
          city: value?.value?.title || value?.value?.address,
          country: 'RS',
        };
      } finally {
        this.isLoading = false;
      }
    }
  },
  methods: {
    ...mapActions("order", [
      "setAlternativeAddress",
      "deleteAlternativeAddress"
    ]),

    // Extract house number from search text 
    extractHouseNumber(text) {
      if (!text) return null;
      const match = text.match(/\b(\d+[a-zA-Z]?)\b/);
      return match ? match[1] : null;
    },

    // Build full address string for address1 field
    buildFullAddress(details, houseNumber) {
      const parts = [];
      if (details.street) parts.push(details.street);
      if (houseNumber || details.houseNumber) parts.push(houseNumber || details.houseNumber);
      if (details.name) parts.push(`(${details.name})`);
      return parts.join(' ') || details.title || details.address || '';
    },

    async fetchAddressSuggestions(searchText) {
      if (!searchText || searchText.trim().length < 2) {
        this.addressSuggestions = [];
        return;
      }
      this.isLoading = true;
      try {
        const coords = this.selectedCoordinates
          ? { lat: this.selectedCoordinates.lat, lng: this.selectedCoordinates.lon }
          : null;

        const results = await fetchAddressSuggestionsPlanPlusV2(searchText, coords);
        this.addressSuggestions = results.map((result) => ({
          label: result.address || result.title,
          value: {
            ...result,
            apiType: 'Planplus',
          },
        }));
      } catch (e) {
        this.addressSuggestions = [];
        this.alertText = 'Error fetching address suggestions';
      } finally {
        this.isLoading = false;
      }
    },

    async resolveStreetDetails(token, houseNumber) {
      this.isLoading = true;
      try {
        const details = await fetchStreetDetailsPlanPlusV2(token, houseNumber);
        if (details) {
          this.foundLocation = {
            apiType: 'Planplus',
            country: details.country || 'RS',
            city: details.place || details.municipality || '',
            postal_code: details.postalCode || null,
            address1: this.buildFullAddress(details, houseNumber),
            lat: details.lat || null,
            lon: details.lng || null,
          };
          if (details.lat && details.lng) {
            this.selectedCoordinates = { lat: details.lat, lon: details.lng };
            this.alertText = '';
          } else {
            this.selectedCoordinates = null;
            this.alertText = 'Location found but coordinates missing. Please select location on map.';
          }
        }
      } catch (e) {
        this.alertText = 'Error resolving street details';
      } finally {
        this.isLoading = false;
      }
    },

    async saveAlternativeAddress() {
      this.isSaving = true;
      
      // Extract house number from address1 (e.g., "Golsvordijeva 28" -> "28")
      let houseNumber = '';
      let streetName = this.foundLocation?.address1 || '';
      
      if (streetName) {
        const match = streetName.match(/\b(\d+[a-zA-Z]?)\b/);
        if (match) {
          houseNumber = match[1];
          // Remove house number from street name to keep only street
          streetName = streetName.replace(/\s*\b\d+[a-zA-Z]?\b\s*/, '').trim();
        }
      }
      
      // Fallback: if no house number found, use "0" to satisfy backend validation
      if (!houseNumber) {
        houseNumber = '0';
      }
      
      const addressObj = {
        order_id: this.orderId,
        country: this.foundLocation?.country || 'RS',
        city: this.foundLocation?.city || null,
        postal_code: this.foundLocation?.postal_code || null,
        city_details: '',
        address1: streetName, // Street name without number
        house_number: houseNumber, // Just the number
        floor_details: '',
        appartment_details: '',
        lat: this.selectedCoordinates?.lat || this.foundLocation?.lat || null,
        lon: this.selectedCoordinates?.lon || this.foundLocation?.lon || null,
        is_user_change: this.isUserChange,
      };

      const isUpdated = await this.setAlternativeAddress(addressObj);
      this.isSaving = false;
      if (isUpdated) {
        this.$emit('is-changed', { orderId: this.orderId, addressObj });
        this.closeDialog();
      } else {
        this.alertText = this.$t('Common.errors.unknown-error');
      }
    },

    async deleteAlternativeAddressLocal() {
      const isUpdated = await this.deleteAlternativeAddress({ order_id: this.orderId, is_user_change: this.isUserChange });
      this.isSaving = false;
      if (isUpdated) {
        this.$emit('is-changed', { orderId: this.orderId, addressObject: null });
        this.closeDialog();
      } else {
        this.alertText = this.$t('Common.errors.unknown-error');
      }
    },

    closeDialog() {
      this.dialog = false;
      this.searchText = null;
      this.selectedAddress = null;
      this.selectedCoordinates = null;
      this.foundLocation = null;
      this.addressSuggestions = [];
      this.isLoading = false;
      this.isSaving = false;
      this.alertText = '';
    },
  }
}
</script>